import { useEffect, useState } from "react";
import { X, RefreshCw, Wallet, CalendarCheck, History } from "lucide-react";
import { Tip } from "@/components/Tip";
import { cn } from "@/lib/utils";
import {
  UserInfo,
  Transaction,
  activateActiveDay,
  fetchTransactions,
  fetchMe,
  ApiError,
} from "@/lib/api";

interface BillingPanelProps {
  user: UserInfo;
  onClose: () => void;
  onUserUpdated: (u: UserInfo) => void;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Billing Phase A (decision #7): tariff status + Active Day activation +
// transaction history. Phase B (payment/top-up/renewal/receipts) is OUT of
// scope — deliberately not rendered.
export function BillingPanel({ user, onClose, onUserUpdated }: BillingPanelProps) {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [activating, setActivating] = useState(false);
  const [notice, setNotice] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const loadTx = () => {
    fetchTransactions(50).then(setTransactions).catch(() => setTransactions([]));
  };
  useEffect(loadTx, []);

  const handleActivate = async () => {
    setActivating(true);
    setNotice(null);
    try {
      const res = await activateActiveDay("ACTIVE_CHAT");
      setNotice({
        kind: "ok",
        text: res?.message || "Active Day активирован.",
      });
      // refresh user (active_day_used / active_days_this_month) + ledger
      const updated = await fetchMe();
      onUserUpdated(updated);
      loadTx();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Не удалось активировать.";
      setNotice({ kind: "err", text: msg });
    } finally {
      setActivating(false);
    }
  };

  const tariff = user.tariff_code ?? null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center md:p-4">
      {/* Scrim */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />
      {/* Panel — full-screen sheet on mobile, centered card on md+ */}
      <div className="relative flex flex-col w-full h-full md:max-w-2xl md:max-h-[85vh] md:h-auto md:rounded-2xl border border-strongline bg-app shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-hairline">
          <h2 className="text-lg font-medium text-primary flex items-center gap-2">
            <Wallet size={18} className="text-accent" /> Финансы
          </h2>
          <Tip label="Закрыть" side="bottom">
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-tertiary hover:text-primary hover:bg-hoverbg transition-colors"
            >
              <X size={18} />
            </button>
          </Tip>
        </div>

        <div className="flex-1 overflow-y-auto px-5 md:px-6 py-5 space-y-6">
          {/* Tariff status */}
          <section>
            <h3 className="text-xs font-medium text-tertiary uppercase tracking-wider mb-3">
              Тариф
            </h3>
            <div className="rounded-xl border border-hairline bg-hoverbg/40 p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-secondary">План</span>
                <span
                  className={cn(
                    "text-sm font-medium",
                    tariff ? "text-cyan-300" : "text-primary"
                  )}
                >
                  {tariff ?? "Free"}
                </span>
              </div>
              {tariff && user.tariff_ends_at && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-secondary">Действует до</span>
                  <span className="text-sm text-primary">
                    {fmtDate(user.tariff_ends_at)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-secondary">Баланс</span>
                <span className="text-sm text-primary">
                  {user.balance ?? 0} монет
                </span>
              </div>
              {!tariff && (
                <p className="text-xs text-tertiary pt-1">
                  Тариф не назначен. Назначение тарифа выполняется
                  администратором (RCC).
                </p>
              )}
            </div>
          </section>

          {/* Active Day */}
          <section>
            <h3 className="text-xs font-medium text-tertiary uppercase tracking-wider mb-3">
              Active Day
            </h3>
            <div className="rounded-xl border border-hairline bg-hoverbg/40 p-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-secondary">Использован сегодня</span>
                <span className={user.active_day_used ? "text-red-400" : "text-emerald-400"}>
                  {user.active_day_used ? "Да" : "Нет"}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-secondary">Активаций в этом месяце</span>
                <span className="text-primary">{user.active_days_this_month ?? 0}</span>
              </div>

              {notice && (
                <div
                  className={cn(
                    "text-xs px-3 py-2 rounded-lg",
                    notice.kind === "ok"
                      ? "bg-emerald-500/10 text-emerald-300"
                      : "bg-red-500/10 text-red-300"
                  )}
                >
                  {notice.text}
                </div>
              )}

              <button
                onClick={handleActivate}
                disabled={user.active_day_used || activating || !tariff}
                className={cn(
                  "w-full h-10 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors",
                  user.active_day_used || activating || !tariff
                    ? "bg-hoverbg text-tertiary cursor-not-allowed"
                    : "bg-accent text-accent-contrast hover:opacity-90"
                )}
              >
                <CalendarCheck size={16} />
                {activating
                  ? "Активация..."
                  : user.active_day_used
                    ? "Использован сегодня"
                    : !tariff
                      ? "Нужен тариф"
                      : "Активировать Active Day"}
              </button>
            </div>
          </section>

          {/* History */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-medium text-tertiary uppercase tracking-wider flex items-center gap-1.5">
                <History size={13} /> История операций
              </h3>
              <Tip label="Обновить" side="top">
                <button
                  onClick={loadTx}
                  className="text-tertiary hover:text-primary transition-colors"
                >
                  <RefreshCw size={14} />
                </button>
              </Tip>
            </div>
            {transactions.length === 0 ? (
              <div className="rounded-xl border border-hairline bg-hoverbg/40 px-4 py-6 text-center text-sm text-tertiary">
                Операций нет
              </div>
            ) : (
              <div className="rounded-xl border border-hairline overflow-hidden divide-y divide-hairline">
                {transactions.map((t) => (
                  <div key={t.id} className="px-4 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm text-primary truncate">{t.description}</div>
                      <div className="text-xs text-tertiary">{fmtDate(t.created_at)}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={cn("text-sm font-medium", t.amount < 0 ? "text-red-400" : "text-emerald-400")}>
                        {t.amount > 0 ? "+" : ""}
                        {t.amount}
                      </div>
                      <div className="text-xs text-tertiary">
                        {t.transaction_type} · {t.balance_after}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}